import { useMemo, useState } from "react";
import { useLang } from "../LanguageContext";
import { translations } from "../data/translations";
import { acbelProducts, acbelCatalogCrpsProducts } from "../data/acbelProducts";
import { seasonicProducts } from "../data/seasonic-industrial";
import ProductCard from "./ProductCard";
import { motion } from "framer-motion";
import { Zap } from "lucide-react";

const INITIAL_VISIBLE = 8;

export default function IndustrialPsuSection() {
  const { lang } = useLang();
  const t = translations[lang] || translations.de;

  const [brandFilter, setBrandFilter] = useState("acbel");
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const brandTabs = [
    { key: "acbel", label: t.industrialPsu?.brandAcbel || "AcBel" },
    { key: "seasonic", label: t.industrialPsu?.brandSeasonic || "Seasonic Industrial" },
  ];

  const filteredProducts = useMemo(() => {
    const acbelAll = [...acbelProducts, ...acbelCatalogCrpsProducts];

    if (brandFilter === "acbel") return acbelAll;
    if (brandFilter === "seasonic") return seasonicProducts;
    return [];
  }, [brandFilter]);

  const visibleProducts = filteredProducts.slice(0, visibleCount);
  const hasMore = visibleCount < filteredProducts.length;

  const handleBrandChange = (brand) => {
    setBrandFilter(brand);
    setVisibleCount(INITIAL_VISIBLE);
  };

  return (
    <section id="industrial-psu" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-4">
            <Zap className="w-10 h-10 text-brand-blue" />
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-3">
            {t.industrialPsu?.title || "Industrial Power Supplies"}
          </h2>

          <p className="text-gray-600 max-w-3xl mx-auto">
            {t.industrialPsu?.description ||
              "Industrial and server-grade power supplies for high-availability, embedded, storage and edge deployments."}
          </p>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {brandTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleBrandChange(tab.key)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                brandFilter === tab.key
                  ? "bg-brand-blue text-white shadow-md"
                  : "bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200 shadow-sm"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="text-center mb-6 text-sm text-gray-500">
          {filteredProducts.length}{" "}
          {filteredProducts.length === 1
            ? t.products?.resultSingle || "Produkt"
            : t.products?.results || "Produkte"}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {visibleProducts.map((product, idx) => (
            <ProductCard key={product.id} product={product} index={idx} />
          ))}
        </div>

        {hasMore && (
          <div className="flex justify-center mt-10">
            <button
              onClick={() => setVisibleCount((prev) => prev + INITIAL_VISIBLE)}
              className="px-6 py-3 rounded-full bg-brand-blue text-white font-medium shadow hover:opacity-90 transition"
            >
              {t.products?.showMore || "Mehr anzeigen"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
